import React, { useState, useEffect } from "react";
import { X, Upload } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import axios from "axios";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import { useClickOutside } from "../../Hooks/useClickOutside";

const AddOrEditDiscountModal = ({ isOpen, onClose, onSuccess, data }) => {
    const isEditing = Boolean(data?._id);

    const [formData, setFormData] = useState({
        title: "",
        subtitle: "",
        discountText: "",
        color: "#4F46E5",
        image: "",
        backgroundImage: "",
        bannerType: "small",
        displayOrder: 0
    });

    const [imageFile, setImageFile] = useState(null);
    const [bgFile, setBgFile] = useState(null);
    const [loading, setLoading] = useState(false);

    const modalRef = useClickOutside(() => onClose());

    // FIXED: Include bannerType and displayOrder in edit mode
    useEffect(() => {
        if (isEditing && data) {
            setFormData({
                title: data.title || "",
                subtitle: data.subtitle || "",
                discountText: data.discountText || "",
                color: data.color || "#4F46E5",
                image: data.image || "",
                backgroundImage: data.backgroundImage || "",
                bannerType: data.bannerType || "small", // FIXED: Added this
                displayOrder: data.displayOrder || 0 // FIXED: Added this
            });
        } else {
            setFormData({
                title: "",
                subtitle: "",
                discountText: "",
                color: "#4F46E5",
                image: "",
                backgroundImage: "",
                bannerType: "small",
                displayOrder: 0
            });
        }

        setImageFile(null);
        setBgFile(null);
    }, [data, isEditing]);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleMainImg = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 1000 * 1024) {
                Swal.fire({
                    icon: "error",
                    title: "File too large",
                    text: "Maximum file size is 1000KB",
                });
                return;
            }
            setImageFile(file);
            setFormData((prev) => ({
                ...prev,
                image: URL.createObjectURL(file),
            }));
        }
    };

    const handleBgImg = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 1000 * 1024) {
                Swal.fire({
                    icon: "error",
                    title: "File too large",
                    text: "Maximum file size is 1000KB",
                });
                return;
            }
            setBgFile(file);
            setFormData((prev) => ({
                ...prev,
                backgroundImage: URL.createObjectURL(file),
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.subtitle || !formData.discountText) {
            Swal.fire({
                icon: "error",
                title: "Missing Fields",
                text: "Please fill in all required fields",
            });
            return;
        }

        try {
            setLoading(true);

            let finalMainImage = formData.image;
            let finalBg = formData.backgroundImage;

            if (imageFile) {
                const uploaded = await uploadToCloudinary(imageFile);
                finalMainImage = uploaded.secure_url || uploaded.url;
            }

            if (bgFile) {
                const uploaded = await uploadToCloudinary(bgFile);
                finalBg = uploaded.secure_url || uploaded.url;
            }

            // FIXED: Include bannerType and displayOrder in payload
            const payload = {
                title: formData.title,
                subtitle: formData.subtitle,
                discountText: formData.discountText,
                color: formData.color,
                image: finalMainImage,
                backgroundImage: finalBg,
                bannerType: formData.bannerType,
                displayOrder: parseInt(formData.displayOrder) || 0
            };

            if (isEditing) {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/discount/edit-discount/${data._id}`,
                    payload
                );
                toast.success("Discount offer updated successfully!");
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/discount/create-discount`,
                    payload
                );
                toast.success("Discount offer created successfully!");
            }

            onSuccess();
            onClose();
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Error!",
                text: err.response?.data?.message || "Something went wrong",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Background Overlay */}
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    />

                    {/* Modal */}
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                    >
                        <div
                            ref={modalRef}
                            className="
                bg-white rounded-2xl shadow-2xl w-full max-w-5xl p-6 relative 
                max-h-[90vh] overflow-y-auto
              "
                        >
                            {/* Close Button */}
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            {/* Header */}
                            <div className="text-center mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">
                                    {isEditing ? "Edit Discount Offer" : "Create New Discount Offer"}
                                </h2>
                                <p className="text-gray-600 mt-1">
                                    {isEditing
                                        ? "Update your discount offer details"
                                        : "Add a new discount offer to your platform"}
                                </p>
                            </div>

                            {/* Grid Layout */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                                {/* LEFT FORM */}
                                <form onSubmit={handleSubmit} className="space-y-6">

                                    {/* Title */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">Offer Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInput}
                                            placeholder="Enter offer title"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            required
                                        />
                                    </div>

                                    {/* Subtitle */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">Subtitle *</label>
                                        <input
                                            type="text"
                                            name="subtitle"
                                            value={formData.subtitle}
                                            onChange={handleInput}
                                            placeholder="Enter subtitle"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            required
                                        />
                                    </div>

                                    {/* Discount Text */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">Discount Text *</label>
                                        <input
                                            type="text"
                                            name="discountText"
                                            value={formData.discountText}
                                            onChange={handleInput}
                                            placeholder="e.g., 40% OFF, FLAT 50% OFF"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            required
                                        />
                                    </div>

                                    {/* Background Color */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">Card Background Color</label>
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="color"
                                                name="color"
                                                value={formData.color}
                                                onChange={handleInput}
                                                className="w-12 h-12 rounded-lg border border-gray-300 cursor-pointer"
                                            />
                                            <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                                                <span className="text-gray-500">#</span>
                                                <span className="text-sm font-mono text-gray-900">
                                                    {formData.color.replace('#', '').toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Banner Type */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                                            Banner Type *
                                        </label>
                                        <select
                                            name="bannerType"
                                            value={formData.bannerType}
                                            onChange={handleInput}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            required
                                        >
                                            <option value="small">Small Banner</option>
                                            <option value="big">Big Banner</option>
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Small banners appear in grid (4 items), Big banners appear as featured (2 items)
                                        </p>
                                    </div>

                                    {/* Display Order */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                                            Display Order
                                        </label>
                                        <input
                                            type="number"
                                            name="displayOrder"
                                            value={formData.displayOrder}
                                            onChange={handleInput}
                                            placeholder="0"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Lower numbers appear first
                                        </p>
                                    </div>

                                    {/* Upload images */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                        {/* Background image */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                                Background Image
                                            </label>
                                            <label className="block w-full border-2 border-dashed border-gray-300 rounded-lg h-32 overflow-hidden cursor-pointer hover:border-blue-500 transition-colors">
                                                {formData.backgroundImage ? (
                                                    <div className="relative w-full h-full">
                                                        <img
                                                            src={formData.backgroundImage}
                                                            alt="Background preview"
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                                            <Upload className="w-6 h-6 text-white" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                                        <Upload className="w-8 h-8 mb-1" />
                                                        <span className="text-sm">Upload Background</span>
                                                        <span className="text-xs mt-1">Max size: 1000KB</span>
                                                    </div>
                                                )}
                                                <input type="file" className="hidden" accept="image/*" onChange={handleBgImg} />
                                            </label>
                                        </div>

                                        {/* Main image */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                                Main Image
                                            </label>
                                            <label className="block w-full border-2 border-dashed border-gray-300 rounded-lg h-32 overflow-hidden cursor-pointer hover:border-blue-500 transition-colors">
                                                {formData.image ? (
                                                    <div className="relative w-full h-full">
                                                        <img
                                                            src={formData.image}
                                                            alt="Main image preview"
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                                            <Upload className="w-6 h-6 text-white" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                                        <Upload className="w-8 h-8 mb-1" />
                                                        <span className="text-sm">Upload Image</span>
                                                        <span className="text-xs mt-1">Max size: 1000KB</span>
                                                    </div>
                                                )}
                                                <input type="file" className="hidden" accept="image/*" onChange={handleMainImg} />
                                            </label>
                                        </div>

                                    </div>

                                    {/* Buttons */}
                                    <div className="flex justify-end pt-4">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors mr-3"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-6 py-3 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {loading ? "Saving..." : isEditing ? "Update Offer" : "Create Offer"}
                                        </button>
                                    </div>
                                </form>

                                {/* RIGHT PREVIEW */}
                                <div className="lg:border-l lg:pl-8">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Card Preview</h3>

                                    <div
                                        className="
                      w-full min-h-[260px] rounded-xl shadow-lg p-6 
                      flex flex-col justify-center items-center relative overflow-hidden border border-gray-200
                    "
                                        style={{
                                            backgroundColor: formData.backgroundImage ? "transparent" : formData.color,
                                            backgroundImage: formData.backgroundImage
                                                ? `url(${formData.backgroundImage})`
                                                : "none",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                        }}
                                    >
                                        {/* Overlay for better text readability */}
                                        {formData.backgroundImage && (
                                            <div className="absolute inset-0 bg-black/30 z-0" />
                                        )}

                                        {/* Content */}
                                        <div className="relative z-10 text-center px-4">

                                            {formData.image && (
                                                <img
                                                    src={formData.image}
                                                    alt="Preview"
                                                    className="h-16 w-16 object-contain mx-auto mb-4"
                                                />
                                            )}

                                            <p className="text-white text-2xl font-bold drop-shadow-lg mb-2">
                                                {formData.discountText || "Discount Text"}
                                            </p>
                                            <p className="text-white text-xl font-semibold drop-shadow-lg mb-1">
                                                {formData.title || "Offer Title"}
                                            </p>
                                            <p className="text-white opacity-90 drop-shadow-lg">
                                                {formData.subtitle || "Offer subtitle"}
                                            </p>
                                        </div>
                                    </div>

                                    <p className="mt-4 text-center text-sm text-gray-500">
                                        This is how your discount card will appear to users
                                    </p>

                                    {/* Banner Type Info */}
                                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                        <p className="text-sm text-blue-800">
                                            <strong>Banner Type:</strong> {formData.bannerType === 'big' ? 'Big Banner (Featured)' : 'Small Banner (Grid)'}
                                        </p>
                                        <p className="text-sm text-blue-800 mt-1">
                                            <strong>Display Order:</strong> {formData.displayOrder}
                                        </p>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default AddOrEditDiscountModal;